/**
 * Gift-with-purchase: syncs cart lines from rules in #gwp-rules (see sections/gwp-rules.liquid).
 * Loaded via `snippets/yo-deferred-asset.liquid`; the top-level `__yoLoaded.gwpCart` guard
 * is the asset-level layer of that snippet's triple guard. Critical here because this script
 * monkey-patches `window.fetch` -- wrapping it twice would double-add gift line items.
 */
(function () {
  window.__yoLoaded = window.__yoLoaded || {};
  if (window.__yoLoaded.gwpCart) return;
  window.__yoLoaded.gwpCart = true;

  const RULES_SCRIPT_ID = "gwp-rules";
  const INTERNAL_HEADER = "X-GWP-Internal";
  let isGwpSyncing = false;
  let syncTail = Promise.resolve();
  let syncTimer = null;
  let needsResync = false;

  function getCartRoot() {
    if (typeof Shopify !== "undefined" && Shopify.routes && Shopify.routes.root) {
      return String(Shopify.routes.root).replace(/\/?$/, "/");
    }
    return "/";
  }

  function cartPath(suffix) {
    return getCartRoot() + suffix.replace(/^\//, "");
  }

  function isCartEndpointUrl(url) {
    if (!url || typeof url !== "string") {
      return false;
    }
    return (
      url.indexOf("/cart/add") !== -1 ||
      url.indexOf("/cart/change") !== -1 ||
      url.indexOf("/cart/update") !== -1 ||
      url.indexOf("/cart/clear") !== -1
    );
  }

  function isInternalFetch(args) {
    try {
      const init = args[1];
      if (!init || !init.headers) return false;
      if (typeof init.headers.get === "function") {
        return init.headers.get(INTERNAL_HEADER) === "1";
      }
      if (init.headers[INTERNAL_HEADER] === "1") {
        return true;
      }
    } catch (e) {}
    return false;
  }

  function requestUrlFromFetchArgs(args) {
    const input = args[0];
    if (typeof input === "string") {
      return input;
    }
    if (typeof Request !== "undefined" && input instanceof Request) {
      return input.url;
    }
    if (input && typeof input.url === "string") {
      return input.url;
    }
    return "";
  }

  function thresholdBasisCents(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }
    let sum = 0;
    for (const item of cart.items) {
      if (!item || (item.quantity || 0) <= 0) {
        continue;
      }
      const fp = item.final_line_price;
      if (typeof fp === "number" && !Number.isNaN(fp)) {
        sum += fp;
      }
    }
    let cartLevel = 0;
    const apps = cart.cart_level_discount_applications;
    if (Array.isArray(apps)) {
      for (const d of apps) {
        const a = d.total_allocated_amount;
        if (typeof a === "number" && !Number.isNaN(a)) {
          cartLevel += a;
        }
      }
    }
    const net = sum - cartLevel;
    return net > 0 ? net : 0;
  }

  function parseRules() {
    const el = document.getElementById(RULES_SCRIPT_ID);
    if (!el || !el.textContent) {
      return [];
    }
    try {
      const rules = JSON.parse(el.textContent.trim());
      return Array.isArray(rules) ? rules : [];
    } catch (e) {
      console.warn("[GWP] Invalid rules JSON", e);
      return [];
    }
  }

  function hasSellingPlan(item) {
    if (!item) {
      return false;
    }
    if (item.selling_plan_allocation) {
      if (item.selling_plan_allocation.selling_plan != null) {
        return true;
      }
      if (item.selling_plan_allocation.selling_plan_id != null) {
        return true;
      }
      if (typeof item.selling_plan_allocation === "object" && Object.keys(item.selling_plan_allocation).length > 0) {
        return true;
      }
    }
    if (item.selling_plan != null && item.selling_plan !== "" && item.selling_plan !== 0 && item.selling_plan !== "0") {
      return true;
    }
    return false;
  }

  function getRuleVariantIdSet(rule) {
    if (!rule) {
      return null;
    }
    const raw = rule.primaryVariantIds;
    if (Array.isArray(raw)) {
      const set = new Set();
      for (let i = 0; i < raw.length; i++) {
        const v = String(raw[i]).trim();
        if (v) {
          set.add(v);
        }
      }
      return set.size ? set : null;
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      const parts = raw.split(",");
      const set = new Set();
      for (let i = 0; i < parts.length; i++) {
        const v = parts[i].trim();
        if (v) {
          set.add(v);
        }
      }
      return set.size ? set : null;
    }
    return null;
  }

  function cartHasEligiblePairItem(cart, rule) {
    const pid = String(rule.primaryProductId);
    const requireSub = Boolean(rule.requireSubscription);
    const allowedVariantIds = getRuleVariantIdSet(rule);

    return (cart.items || []).some(function (item) {
      if (!item || (item.quantity || 0) <= 0) {
        return false;
      }
      if (String(item.product_id) !== pid) {
        return false;
      }
      if (allowedVariantIds && !allowedVariantIds.has(String(item.variant_id))) {
        const vTitle = String(item.variant_title || "").toLowerCase();
        let titleMatch = false;
        if (vTitle) {
          for (const val of allowedVariantIds) {
            const vLower = val.toLowerCase().trim();
            if (vLower && vTitle.indexOf(vLower) !== -1) {
              titleMatch = true;
              break;
            }
          }
        }
        if (!titleMatch) {
          return false;
        }
      }
      if (requireSub && !hasSellingPlan(item)) {
        return false;
      }
      return true;
    });
  }

  function meetsThreshold(cart, thresholdCents) {
    return thresholdBasisCents(cart) >= thresholdCents;
  }

  function ruleQualified(cart, rule) {
    if (rule.type === "pair") {
      return cartHasEligiblePairItem(cart, rule);
    }
    if (rule.type === "threshold") {
      return meetsThreshold(cart, rule.thresholdCents);
    }
    return false;
  }

  function findManagedLine(cart, rule) {
    const vid = String(rule.giftVariantId);
    const rid = String(rule.ruleId);
    const items = cart.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || (item.quantity || 0) <= 0) {
        continue;
      }
      if (!item.properties || String(item.variant_id) !== vid) {
        continue;
      }
      if (String(item.properties._gwp_rule_id || "") !== rid) {
        continue;
      }
      if (!item.properties._gwp_managed) {
        continue;
      }
      return item;
    }
    return null;
  }

  function isAddBlocked(cart, rule, pendingRemovals) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const removalKeys = pendingRemovals || null;
    if (rule.limitOnePerOrder !== false) {
      const vid = String(rule.giftVariantId);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item || (item.quantity || 0) <= 0) continue;
        if (removalKeys && item.key && removalKeys.has(item.key)) continue;
        if (String(item.variant_id) === vid) {
          return true;
        }
      }
    }
    if (rule.blockIfProductId != null && rule.blockIfProductId !== "") {
      const bpid = String(rule.blockIfProductId);
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item || (item.quantity || 0) <= 0) continue;
        if (removalKeys && item.key && removalKeys.has(item.key)) continue;
        if (String(item.product_id) === bpid) {
          return true;
        }
      }
    }
    return false;
  }

  function fetchCart() {
    const sep = cartPath("cart.js").indexOf("?") === -1 ? "?" : "&";
    const headers = {
      Accept: "application/json",
      "Cache-Control": "no-cache"
    };
    headers[INTERNAL_HEADER] = "1";
    return fetch(cartPath("cart.js") + sep + "_t=" + Date.now(), {
      cache: "no-store",
      credentials: "same-origin",
      headers: headers
    }).then(function (res) {
      if (!res.ok) {
        throw new Error("[GWP] cart.js HTTP " + res.status);
      }
      return res.json();
    });
  }

  function postJson(url, body) {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    headers[INTERNAL_HEADER] = "1";
    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: headers,
      body: JSON.stringify(body)
    });
  }

  function dispatchCartRefresh() {
    try {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } catch (e) {}
  }

  function buildGiftProperties(rule) {
    const props = {
      _gwp_managed: "1",
      _gwp_rule: rule && rule.type ? rule.type : "pair",
      _gwp_rule_id: rule && rule.ruleId != null ? String(rule.ruleId) : "",
      _gwp: "Free Gift"
    };
    if (rule && rule.primaryProductHandle) {
      props._gwp_paired_with = String(rule.primaryProductHandle);
    }
    return props;
  }

  /**
   * One pass: apply batched line updates, then sequential adds. Returns true if any request succeeded.
   */
  function applyPlan(cart, rules) {
    const updates = {};
    const adds = [];

    const knownRuleIds = new Set();
    for (let r = 0; r < rules.length; r++) {
      const rule = rules[r];
      if (rule && rule.ruleId != null) {
        knownRuleIds.add(String(rule.ruleId));
      }
      const qualified = ruleQualified(cart, rule);
      const line = findManagedLine(cart, rule);

      if (!qualified) {
        if (line) {
          updates[line.key] = 0;
        }
        continue;
      }

      if (line && line.quantity !== 1) {
        updates[line.key] = 1;
      }
    }

    /*
     * Orphan sweep: drop any _gwp_managed line whose source rule has been deleted or hidden
     * in the gwp-rules section (hidden blocks are excluded from section.blocks at render
     * time, so they look identical to deletions from this script's perspective). Lines whose
     * rule still exists but is unqualified (cart no longer qualifies, etc.) are handled by
     * the rule loop above. syncGwp short-circuits when rules.length === 0, so this only
     * runs when at least one rule is configured -- that prevents a missing or broken rules
     * block from wiping every shopper's gift line.
     */
    const cartItems = cart.items || [];
    for (let i = 0; i < cartItems.length; i++) {
      const item = cartItems[i];
      if (!item || !item.properties || !item.properties._gwp_managed) {
        continue;
      }
      if (Object.prototype.hasOwnProperty.call(updates, item.key)) {
        continue;
      }
      const lineRuleId = item.properties._gwp_rule_id == null ? "" : String(item.properties._gwp_rule_id);
      if (lineRuleId && knownRuleIds.has(lineRuleId)) {
        continue;
      }
      updates[item.key] = 0;
    }

    const pendingRemovalKeys = new Set();
    for (const k of Object.keys(updates)) {
      if (updates[k] === 0) {
        pendingRemovalKeys.add(k);
      }
    }

    for (let r = 0; r < rules.length; r++) {
      const rule = rules[r];
      const qualified = ruleQualified(cart, rule);
      const line = findManagedLine(cart, rule);

      if (qualified && !line) {
        if (isAddBlocked(cart, rule, pendingRemovalKeys)) {
          continue;
        }
        adds.push(rule);
      }
    }

    const updateKeys = Object.keys(updates);

    return Promise.resolve()
      .then(function () {
        if (!updateKeys.length) {
          return false;
        }
        return postJson(cartPath("cart/update.js"), { updates: updates }).then(function (res) {
          if (!res.ok) {
            console.warn("[GWP] cart/update.js failed", res.status);
            return false;
          }
          return true;
        });
      })
      .then(function (didUpdate) {
        if (!adds.length) {
          return didUpdate;
        }
        return fetchCart().then(function (fresh) {
          let anyAdd = false;

          function addNext(index) {
            if (index >= adds.length) {
              return Promise.resolve(didUpdate || anyAdd);
            }

            const rule = adds[index];

            if (findManagedLine(fresh, rule)) {
              return addNext(index + 1);
            }

            if (isAddBlocked(fresh, rule)) {
              return addNext(index + 1);
            }

            return postJson(cartPath("cart/add.js"), {
              items: [
                {
                  id: rule.giftVariantId,
                  quantity: 1,
                  properties: buildGiftProperties(rule)
                }
              ]
            })
              .then(function (res) {
                if (!res.ok) {
                  return res
                    .json()
                    .catch(function () {
                      return {};
                    })
                    .then(function (err) {
                      console.warn("[GWP] cart/add.js failed", err);
                      document.dispatchEvent(
                        new CustomEvent("gwp:error", {
                          detail: { rule: rule, error: err }
                        })
                      );
                    });
                }
                anyAdd = true;
                return fetchCart().then(function (c) {
                  fresh = c;
                });
              })
              .then(function () {
                return addNext(index + 1);
              });
          }

          return addNext(0);
        });
      });
  }

  /**
   * Line items for pair rules matching this primary product (same shape as applyPlan adds).
   * Used to batch primary + gifts in one /cart/add.js on landers before checkout redirect.
   * Async because per-rule guards (`limitOnePerOrder`, `blockIfProductId`) need current cart state.
   *
   * @param {string|number} productId
   * @param {string|number} [variantId]
   * @param {string|number|boolean} [sellingPlan]
   */
  function pairGiftLineItemsForProduct(productId, variantId, sellingPlan) {
    const pid = String(productId || "").trim();
    if (!pid) {
      return Promise.resolve([]);
    }
    const rules = parseRules();
    const candidates = [];
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule || rule.type !== "pair" || String(rule.primaryProductId) !== pid) {
        continue;
      }
      const allowedVariantIds = getRuleVariantIdSet(rule);
      if (allowedVariantIds) {
        if (!variantId || !allowedVariantIds.has(String(variantId))) {
          continue;
        }
      }
      if (rule.requireSubscription) {
        const hasSub = Boolean(sellingPlan && sellingPlan !== "" && sellingPlan !== 0 && sellingPlan !== "0");
        if (!hasSub) {
          continue;
        }
      }
      const vid = rule.giftVariantId;
      if (vid == null || vid === "") {
        continue;
      }
      candidates.push(rule);
    }
    if (!candidates.length) {
      return Promise.resolve([]);
    }
    return fetchCart()
      .catch(function () {
        return { items: [] };
      })
      .then(function (cart) {
        const out = [];
        const projectedSiblings = [];
        for (let i = 0; i < candidates.length; i++) {
          const rule = candidates[i];
          const projected = {
            items: (cart.items || []).concat(projectedSiblings)
          };
          if (isAddBlocked(projected, rule)) {
            continue;
          }
          out.push({
            id: Number(rule.giftVariantId),
            quantity: 1,
            properties: buildGiftProperties(rule)
          });
          projectedSiblings.push({
            variant_id: String(rule.giftVariantId),
            product_id: rule.giftProductId != null ? String(rule.giftProductId) : null
          });
        }
        return out;
      });
  }

  function syncGwp() {
    const rules = parseRules();
    if (!rules.length) {
      return Promise.resolve();
    }

    if (isGwpSyncing) {
      needsResync = true;
      return syncTail;
    }

    isGwpSyncing = true;
    const job = syncTail.then(function () {
      return fetchCart()
        .then(function (cart) {
          return applyPlan(cart, rules);
        })
        .then(function (changed) {
          if (changed) {
            dispatchCartRefresh();
          }
        })
        .catch(function (err) {
          console.warn("[GWP] sync error", err);
        })
        .finally(function () {
          isGwpSyncing = false;
          if (needsResync) {
            needsResync = false;
            scheduleSync(true);
          }
        });
    });

    syncTail = job.catch(function () {});
    return job;
  }

  function scheduleSync(immediate) {
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }
    if (immediate) {
      syncGwp();
      return;
    }
    syncTimer = setTimeout(function () {
      syncTimer = null;
      syncGwp();
    }, 75);
  }

  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch;
    window.fetch = function () {
      const isInternal = isInternalFetch(arguments);
      const result = originalFetch.apply(this, arguments);
      const url = requestUrlFromFetchArgs(arguments);
      if (isInternal || !isCartEndpointUrl(url)) {
        return result;
      }
      return result.then(function (response) {
        if (response && response.ok) {
          scheduleSync();
        }
        return response;
      });
    };
  }

  document.addEventListener("cart:change", function () {
    scheduleSync();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      scheduleSync(true);
    }, { once: true });
  } else {
    scheduleSync(true);
  }

  window.GWPCart = window.GWPCart || {};
  window.GWPCart.sync = syncGwp;
  window.GWPCart.pairGiftLineItemsForProduct = pairGiftLineItemsForProduct;
})();
